import React, { useState, useEffect } from 'react';
import { BRAND, NAV_LINKS } from '../data/mockData';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoToContact } from '../hooks/useGoToContact';
import PublicSearchModal from './PublicSearchModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const goToContact = useGoToContact();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cmd/Ctrl + K opens the public search modal anywhere on the public site
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleNavClick = (link) => {
    setMobileOpen(false);
    if (link.isRoute) {
      navigate(link.href);
      return;
    }
    // If we're on the work page, go back to home first
    if (location.pathname !== '/') {
      navigate('/' + link.href);
      return;
    }
    const el = document.querySelector(link.href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    setMobileOpen(false);
    goToContact();
  };

  // Pages that have a dark hero header at the top — keep navbar dark always
  const darkHeaderPages =
    location.pathname === '/work' ||
    location.pathname.startsWith('/insights');

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || darkHeaderPages
            ? 'bg-[#242326]/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex-shrink-0">
              <img
                src={BRAND.logoFull}
                alt="Grouth"
                className="h-12 md:h-14 lg:h-16 w-auto brightness-0 invert"
              />
            </button>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-10">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link)}
                  className={`flex items-center gap-1 text-[15px] font-light tracking-wide transition-colors duration-300 font-['Inter'] ${
                    link.isRoute && location.pathname === link.href
                      ? 'text-[#83BF42]'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                  {link.hasDropdown && <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="navbar-search-trigger"
                aria-label="Open search"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-white/60 hover:text-white border border-white/15 hover:border-white/30 text-xs font-['Inter'] transition-all"
              >
                <Search className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">Search</span>
                <kbd className="hidden xl:inline text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/15 text-white/50">⌘K</kbd>
              </button>
              <button
                onClick={handleContactClick}
                data-testid="navbar-contact-cta"
                className="flex items-center px-6 py-2.5 rounded-full border border-white/30 text-white text-[15px] font-light hover:bg-white hover:text-[#242326] transition-all duration-300 font-['Inter']"
              >
                Get in Touch
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#242326] pt-24 px-6"
          >
            <div className="flex flex-col gap-6">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link)}
                  className="text-white text-2xl font-light text-left font-['Inter'] hover:text-[#83BF42] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
                data-testid="mobile-search-trigger"
                className="text-white/70 text-lg font-light text-left font-['Inter'] hover:text-[#83BF42] transition-colors flex items-center gap-2"
              >
                <Search className="w-4 h-4" /> Search the site
              </button>
              <button
                onClick={handleContactClick}
                className="mt-4 px-8 py-3 rounded-full border border-[#83BF42] text-[#83BF42] text-lg font-['Inter'] hover:bg-[#83BF42] hover:text-white transition-all w-fit"
              >
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <PublicSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;
